import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import Iconify from './Iconify';
import ModalAutorizacaoDesconto from './ModalAutorizacaoDesconto';
import Actions from '../constants/Actions';
import {
    SEM_IMAGEM,
    collectProductImageUrls,
    createImageFallbackHandler,
    getProductThumbUrl,
} from '../utils/productImageUrl';
import { getItemPrecoLiquido } from '../utils/functions';

const money = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`;

const CartItemResumo = ({ cartItem, removeFromCart }) => {
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);

    const precoOriginal = parseFloat(cartItem.regular_price || 0);
    const descontoItem = parseFloat(cartItem.discount_total || 0) || 0;
    const precoLiquido = getItemPrecoLiquido(cartItem);
    const temDesconto = descontoItem > 0;

    const candidates = useMemo(() => {
        const urls = collectProductImageUrls(cartItem);
        return urls.length ? urls : [SEM_IMAGEM];
    }, [cartItem]);
    const imgSrc = getProductThumbUrl(cartItem, SEM_IMAGEM);
    const handleError = createImageFallbackHandler(candidates, SEM_IMAGEM);

    const corLabel = cartItem.use_cor_produto || cartItem.use_cod_cor_produto || '—';
    const metaLine = [
        cartItem.grade ? `Tam ${cartItem.grade}` : null,
        corLabel,
        cartItem.filial ? `Loja ${cartItem.filial}` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    const handleAutorizado = ({ valorReais, autorizacao, modo, valorInformado }) => {
        const discount = Number(Number(valorReais).toFixed(2));
        const price = Math.max(0, Number((precoOriginal - discount).toFixed(2)));

        dispatch({
            type: Actions.UPDATE_CART_ITEM_DISCOUNT,
            payload: {
                product_id: cartItem.product_id,
                grade: cartItem.grade,
                filial: cartItem.filial,
                updates: {
                    discount_total: discount,
                    price,
                    price_total: price,
                    desconto_autorizacao: {
                        ...autorizacao,
                        valorReais: discount,
                        modo,
                        valorInformado,
                        escopo: 'produto',
                    },
                },
            },
        });
    };

    const handleRemoverDesconto = () => {
        dispatch({
            type: Actions.UPDATE_CART_ITEM_DISCOUNT,
            payload: {
                product_id: cartItem.product_id,
                grade: cartItem.grade,
                filial: cartItem.filial,
                updates: {
                    discount_total: 0,
                    price: precoOriginal,
                    price_total: precoOriginal,
                    desconto_autorizacao: null,
                },
            },
        });
    };

    return (
        <Box
            sx={{
                position: 'relative',
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: temDesconto ? 'rgba(22, 163, 74, 0.04)' : 'transparent',
                border: '1px solid',
                borderColor: temDesconto ? 'rgba(22, 163, 74, 0.18)' : 'grey.200',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': {
                    borderColor: temDesconto ? 'rgba(22, 163, 74, 0.35)' : 'grey.300',
                },
                '&:hover .cart-item-remove': { opacity: 1 },
            }}
        >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                    component="img"
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 1.25,
                        objectFit: 'cover',
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        flexShrink: 0,
                    }}
                    src={imgSrc}
                    alt={cartItem.use_descricao}
                    data-fallback-index="0"
                    onError={handleError}
                />

                <Box sx={{ flex: 1, minWidth: 0, pr: 2.5 }}>
                    <Typography
                        sx={{
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            color: 'text.primary',
                            lineHeight: 1.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {cartItem.use_descricao}
                    </Typography>

                    <Typography
                        sx={{
                            mt: 0.35,
                            fontSize: '0.72rem',
                            color: 'text.secondary',
                            lineHeight: 1.3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {metaLine}
                    </Typography>

                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mt: 0.85 }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            {temDesconto ? (
                                <Stack spacing={0.15}>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '0.68rem',
                                            color: 'text.disabled',
                                            textDecoration: 'line-through',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {money(precoOriginal)}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '0.95rem',
                                            fontWeight: 800,
                                            color: 'success.dark',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.15,
                                        }}
                                    >
                                        {money(precoLiquido)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            color: 'success.main',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        −{money(descontoItem)}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: '0.95rem',
                                        fontWeight: 800,
                                        color: 'text.primary',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.15,
                                    }}
                                >
                                    {money(precoOriginal)}
                                </Typography>
                            )}
                        </Box>

                        <Stack direction="row" spacing={0.25} alignItems="center" flexShrink={0}>
                            <Tooltip title={temDesconto ? 'Alterar desconto' : 'Aplicar desconto'}>
                                <IconButton
                                    size="small"
                                    onClick={() => setModalOpen(true)}
                                    aria-label={temDesconto ? 'Alterar desconto' : 'Aplicar desconto'}
                                    sx={{
                                        color: temDesconto ? 'success.main' : 'primary.main',
                                        bgcolor: temDesconto ? 'rgba(22, 163, 74, 0.08)' : 'rgba(25, 118, 210, 0.06)',
                                        borderRadius: 1,
                                        '&:hover': {
                                            bgcolor: temDesconto
                                                ? 'rgba(22, 163, 74, 0.16)'
                                                : 'rgba(25, 118, 210, 0.12)',
                                        },
                                    }}
                                >
                                    <Iconify width={16} icon="mdi:percent-outline" />
                                </IconButton>
                            </Tooltip>
                            {temDesconto && (
                                <Tooltip title="Remover desconto do item">
                                    <IconButton
                                        size="small"
                                        onClick={handleRemoverDesconto}
                                        aria-label="Remover desconto do item"
                                        sx={{
                                            color: 'text.secondary',
                                            borderRadius: 1,
                                            '&:hover': { color: 'error.main', bgcolor: 'error.lighter' },
                                        }}
                                    >
                                        <Iconify width={16} icon="mdi:tag-off-outline" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Stack>

            <Tooltip title="Remover item">
                <IconButton
                    className="cart-item-remove"
                    size="small"
                    onClick={() => removeFromCart(cartItem)}
                    aria-label="Remover item"
                    sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        p: 0.35,
                        color: 'grey.400',
                        opacity: { xs: 1, md: 0.35 },
                        transition: 'opacity 0.15s ease, color 0.15s ease',
                        '&:hover': { color: 'error.main', opacity: 1, bgcolor: 'error.lighter' },
                    }}
                >
                    <Iconify width={15} icon="mdi:close" />
                </IconButton>
            </Tooltip>

            <ModalAutorizacaoDesconto
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                escopo="produto"
                baseValor={precoOriginal}
                titulo="Desconto no produto"
                onAutorizado={handleAutorizado}
            />
        </Box>
    );
};

export default CartItemResumo;
